import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import ForumIcon from "@material-ui/icons/Forum";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { isArray } from "lodash";
import useSound from "use-sound";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import notifySound from "../../assets/chat_notify.mp3";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    color: "white",
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#f04438",
      color: "#fff",
      fontWeight: 600,
      fontSize: 11,
      minWidth: 18,
      height: 18,
      padding: "0 4px",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    },
  },
  popoverPaper: {
    width: "100%",
    maxWidth: 380,
    minWidth: 320,
    marginRight: theme.spacing(1),
    borderRadius: 12,
    overflow: "hidden",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 12px 32px rgba(0,0,0,0.55)"
        : "0 12px 32px rgba(15,23,42,0.18)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(97,114,243,0.18) 0%, rgba(128,152,249,0.08) 100%)"
        : "linear-gradient(135deg, #6172f3 0%, #8098f9 100%)",
    color: theme.palette.type === "dark" ? theme.palette.text.primary : "#fff",
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: 0.2,
  },
  headerCount: {
    backgroundColor: "rgba(255,255,255,0.18)",
    color: "inherit",
    fontWeight: 600,
    height: 22,
  },
  listContainer: {
    maxHeight: 420,
    overflowY: "auto",
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    ...theme.scrollbarStyles,
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  chatItem: {
    alignItems: "flex-start",
    padding: theme.spacing(1.25, 2),
    cursor: "pointer",
    transition: "background-color 120ms ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(97,114,243,0.06)",
    },
  },
  chatItemUnread: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(97,114,243,0.10)"
        : "rgba(97,114,243,0.06)",
  },
  avatar: {
    backgroundColor: "#6172f3",
    color: "#fff",
    width: 40,
    height: 40,
  },
  chatTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  chatTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  chatTime: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  chatBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: 2,
  },
  chatLastMessage: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  chatLastMessageUnread: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  unreadChip: {
    backgroundColor: "#f04438",
    color: "#fff",
    fontWeight: 700,
    fontSize: 11,
    height: 20,
    minWidth: 20,
    "& .MuiChip-label": {
      padding: "0 6px",
    },
  },
  footer: {
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    color: "#6172f3",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(97,114,243,0.06)",
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    }
    return [chat, ...state];
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const incoming = action.payload.chat;
    const idx = state.findIndex((c) => c.id === incoming.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = incoming;
      // Bring updated chat to top
      const [moved] = next.splice(idx, 1);
      return [moved, ...next];
    }
    return [incoming, ...state];
  }

  return state;
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export default function ChatPopover() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { datetimeToClient } = useDate();
  const [play] = useSound(notifySound);
  const soundAlertRef = useRef();
  const originalTitleRef = useRef(
    typeof document !== "undefined" ? document.title : ""
  );
  const desktopNotificationsRef = useRef([]);

  useEffect(() => {
    soundAlertRef.current = play;
    if (!("Notification" in window)) {
      // Browser does not support notifications
    } else if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (!user?.companyId || !socket) return;
    const companyId = user.companyId;

    const onCompanyChatPopover = (data) => {
      if (data.action === "new-message") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
        if (data.newMessage && data.newMessage.senderId !== user.id) {
          if (soundAlertRef.current) {
            try {
              soundAlertRef.current();
            } catch (_) {
              /* ignore */
            }
          }
          showDesktopNotification(data);
        }
      }
      if (data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CHAT", payload: data.id });
      }
    };

    socket.on(`company-${companyId}-chat`, onCompanyChatPopover);
    return () => {
      socket.off(`company-${companyId}-chat`, onCompanyChatPopover);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, socket]);

  const totalUnreads = useMemo(() => {
    let count = 0;
    if (chats.length > 0) {
      for (const chat of chats) {
        if (!isArray(chat.users)) continue;
        for (const chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            count += chatUser.unreads || 0;
          }
        }
      }
    }
    return count;
  }, [chats, user.id]);

  // Update document title with unread chat count
  useEffect(() => {
    if (typeof document === "undefined") return;
    const baseTitle =
      (theme && theme.appName) || originalTitleRef.current || "";
    if (totalUnreads > 0) {
      document.title = `(${totalUnreads}) ${baseTitle}`;
    } else if (baseTitle) {
      document.title = baseTitle;
    }
  }, [totalUnreads, theme]);

  const showDesktopNotification = (data) => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted"
    ) {
      return;
    }
    if (document.visibilityState === "visible") return;

    const chat = data.chat || {};
    const message = data.newMessage || {};
    const title =
      i18n.t("chatInternal.notification.newMessage") || "Nova mensagem";

    try {
      const notification = new Notification(`${title}: ${chat.title || ""}`, {
        body: message.message || "",
        tag: `chat-${chat.id}`,
        renotify: true,
      });
      notification.onclick = (e) => {
        e.preventDefault();
        window.focus();
        if (chat.uuid) {
          history.push(`/chats/${chat.uuid}`);
        }
        notification.close();
      };

      const list = desktopNotificationsRef.current;
      const idx = list.findIndex((n) => n.tag === notification.tag);
      if (idx !== -1) {
        try {
          list[idx].close();
        } catch (_) {}
        list.splice(idx, 1);
      }
      list.unshift(notification);
      desktopNotificationsRef.current = list;
    } catch (_) {
      /* ignore */
    }
  };

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToMessages = async (chat) => {
    handleClose();
    const unreadsForUser = (chat.users || []).find(
      (u) => u.userId === user.id
    );
    if (unreadsForUser && unreadsForUser.unreads > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (_) {
        /* ignore */
      }
    }
    history.push(`/chats/${chat.uuid}`);
  };

  const goToChatList = () => {
    handleClose();
    history.push("/chats");
  };

  const open = Boolean(anchorEl);
  const id = open ? "chat-popover" : undefined;

  const sortedChats = useMemo(() => {
    return [...chats].sort((a, b) => {
      const aUnread =
        (a.users || []).find((u) => u.userId === user.id)?.unreads || 0;
      const bUnread =
        (b.users || []).find((u) => u.userId === user.id)?.unreads || 0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      const aDate = new Date(a.updatedAt || 0).getTime();
      const bDate = new Date(b.updatedAt || 0).getTime();
      return bDate - aDate;
    });
  }, [chats, user.id]);

  const renderChatItem = (chat) => {
    const unreads =
      (chat.users || []).find((u) => u.userId === user.id)?.unreads || 0;
    const hasUnread = unreads > 0;

    return (
      <ListItem
        key={chat.id}
        button
        className={`${classes.chatItem} ${
          hasUnread ? classes.chatItemUnread : ""
        }`}
        onClick={() => goToMessages(chat)}
      >
        <ListItemAvatar>
          <Avatar className={classes.avatar}>
            {getInitials(chat.title)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <Box className={classes.chatTitleRow}>
              <Typography className={classes.chatTitle} title={chat.title}>
                {chat.title}
              </Typography>
              <Typography className={classes.chatTime}>
                {chat.updatedAt ? datetimeToClient(chat.updatedAt) : ""}
              </Typography>
            </Box>
          }
          secondary={
            <Box className={classes.chatBottomRow}>
              <Typography
                className={`${classes.chatLastMessage} ${
                  hasUnread ? classes.chatLastMessageUnread : ""
                }`}
              >
                {chat.lastMessage || "—"}
              </Typography>
              {hasUnread && (
                <Chip
                  size="small"
                  label={unreads > 99 ? "99+" : unreads}
                  className={classes.unreadChip}
                />
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  return (
    <div>
      <Tooltip title={i18n.t("mainDrawer.listItems.chats") || "Chat Interno"}>
        <IconButton
          aria-describedby={id}
          variant="contained"
          onClick={handleClick}
          className={classes.iconButton}
        >
          <Badge
            className={classes.badge}
            overlap="rectangular"
            badgeContent={totalUnreads}
            max={99}
            color="error"
          >
            <ForumIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            {i18n.t("mainDrawer.listItems.chats") || "Chat Interno"}
          </Typography>
          {totalUnreads > 0 && (
            <Chip
              size="small"
              className={classes.headerCount}
              label={`${totalUnreads} ${
                totalUnreads === 1 ? "nova" : "novas"
              }`}
            />
          )}
        </Box>
        <Paper
          variant="outlined"
          onScroll={handleScroll}
          className={classes.listContainer}
          square
          elevation={0}
        >
          {sortedChats.length === 0 ? (
            <Box className={classes.emptyState}>
              <ForumIcon
                style={{ fontSize: 36, opacity: 0.3, marginBottom: 8 }}
              />
              <Typography variant="body2">
                {i18n.t("mainDrawer.appBar.notRegister") || "Nenhuma conversa"}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {sortedChats.map((chat, index) => (
                <React.Fragment key={chat.id}>
                  {renderChatItem(chat)}
                  {index < sortedChats.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
        <Box className={classes.footer} onClick={goToChatList}>
          {i18n.t("chatInternal.openAll") || "Ver todas as conversas"}
        </Box>
      </Popover>
    </div>
  );
}
